import React, { useState } from 'react';
import { List, useTable, EditButton, ShowButton, DeleteButton } from '@refinedev/antd';
import { Table, Space, Tag, Input } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { RestaurantDto } from '@wellverse/shared-ts';
import { ExcelButtons } from '../../components/ExcelButtons';

const tagStyle = (color: string) => ({
  borderColor: color,
  color,
  background: `${color}15`,
  borderRadius: 9999,
  padding: '2px 12px',
  fontWeight: 500,
});

export const RestaurantList: React.FC = () => {
  const [searchText, setSearchText] = useState('');

  const { tableProps } = useTable<RestaurantDto>({
    resource: 'restaurants',
    filters: {
      permanent: [{ field: 'search', operator: 'contains', value: searchText }],
    },
    pagination: { current: 1, pageSize: 20 },
    queryOptions: { keepPreviousData: true },
  });

  return (
    <List resource="restaurants">
      <div style={{ marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center' }}>
        <Input.Search
          prefix={<SearchOutlined />}
          placeholder="Search restaurants..."
          defaultValue={searchText}
          onSearch={(value) => setSearchText(value)}
          allowClear
          style={{ width: 320 }}
        />
        <ExcelButtons resource="restaurant-admin/restaurants" filename="restaurants" />
      </div>
      <Table {...tableProps} rowKey="id" scroll={{ x: 1200 }}>
        <Table.Column
          dataIndex="logoUrl"
          title="Logo"
          width={64}
          render={(url: string) =>
            url ? (
              <img src={url} alt="" style={{ width: 40, height: 40, borderRadius: 8, objectFit: 'cover' }} />
            ) : (
              <div style={{ width: 40, height: 40, borderRadius: 8, background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#bbb', fontSize: 12 }}>—</div>
            )
          }
        />
        <Table.Column dataIndex="name" title="Name" />
        <Table.Column
          dataIndex="translations"
          title="Translations"
          render={(translations: Record<string, { name?: string }>) => {
            if (!translations || Object.keys(translations).length === 0) return '\u2014';
            return (
              <Space wrap size={4}>
                {Object.entries(translations).map(([locale, data]) => (
                  <Tag key={locale} style={{ borderRadius: 9999, padding: '2px 8px' }}>
                    {locale}: {data.name}
                  </Tag>
                ))}
              </Space>
            );
          }}
        />
        <Table.Column dataIndex="cuisineType" title="Cuisine Type" render={(v: string) => v || '\u2014'} />
        <Table.Column
          dataIndex="tags"
          title="Tags"
          render={(tags: string[]) => {
            if (!tags || tags.length === 0) return '\u2014';
            return (
              <Space wrap size={4}>
                {tags.slice(0, 3).map((t) => (
                  <Tag key={t} style={{ borderRadius: 9999, padding: '2px 8px' }}>{t}</Tag>
                ))}
                {tags.length > 3 && <span style={{ color: '#898989' }}>+{tags.length - 3}</span>}
              </Space>
            );
          }}
        />
        <Table.Column
          dataIndex="mealCount"
          title="Meals"
          render={(v: number) => v ?? 0}
        />
        <Table.Column
          dataIndex="isActive"
          title="Active"
          filters={[
            { text: 'Active', value: true },
            { text: 'Inactive', value: false },
          ]}
          onFilter={(value, record: any) => record.isActive === value}
          render={(isActive: boolean) => (
            <Tag style={tagStyle(isActive ? '#3ecf8e' : '#898989')}>
              {isActive ? 'Active' : 'Inactive'}
            </Tag>
          )}
        />
        <Table.Column
          title="Actions"
          dataIndex="actions"
          render={(_: any, record: RestaurantDto) => (
            <Space>
              <EditButton hideText size="small" recordItemId={record.id} resource="restaurants" />
              <ShowButton hideText size="small" recordItemId={record.id} resource="restaurants" />
              <DeleteButton hideText size="small" recordItemId={record.id} resource="restaurants" />
            </Space>
          )}
        />
      </Table>
    </List>
  );
};
